import { PlanType } from './../../../models/orders';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as TradingActions from '../actions/trading.actions';
import * as PortfolioActions from '../../portfolios/actions/portfolios.actions';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { TradingService } from 'src/app/services/http/trading.service';
import {
    InvestmentPlan,
    investmentPlanPeriodTypes,
    Order,
    OrderAction,
    OrderType,
    Product,
    RegularInvestmentStatus,
} from 'src/app/models/orders';
import { of } from 'rxjs';
import { AppConfig } from 'src/app/app.config';
import { Features } from 'src/app/models/features';
import * as GlobalNotificationActions from './../../global-notification/actions/global-notification.action';
import { TradingFacades } from '../facades/trading.facades';
import { SnackBarService } from 'src/app/services/snackbar/snackBar.service';
import { IRegularInvestmentPlanSetting } from 'src/app/models/featuresConfig';
import { deduplicatingSwitchMap } from 'src/app/utils/deduplicatingSwitchMap';
import {DecimalPipe} from "@angular/common";

@Injectable()
export class TradingEffects {
    constructor(
        private actions$: Actions,
        private tradingService: TradingService,
        private tradingFacade: TradingFacades,
        private appConfig: AppConfig,
        private snackBarService: SnackBarService,
        private decimalPipe: DecimalPipe,
    ) {}

    getProducts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TradingActions.GET_PRODUCTS_LIST),
            switchMap((action: TradingActions.GetAvailableProducts) => {
                return this.tradingService.getProducts(action.payload).pipe(
                    map((res: Product[]) => {
                        if (res) {
                            return new TradingActions.GetAvailableProductsSuccess(
                                res,
                            );
                        }
                    }),
                    catchError(() => {
                        this.snackBarService.showWarningSnackBar(
                            'We were unable to retrieve the products list please try again.',
                        );
                        return of(new TradingActions.UpdateStatus());
                    }),
                );
            }),
        ),
    );

    getOrders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TradingActions.GET_CURRENT_ORDERS_LIST),
            switchMap((action: TradingActions.GetCurrentOrders) => {
                return this.tradingService
                    .getPortfoliosOrders(action.payload)
                    .pipe(
                        map((res: Order[]) => {
                            if (res) {
                                return new TradingActions.GetCurrentOrdersSuccess(
                                    res,
                                );
                            }
                        }),
                        catchError(() => {
                            this.snackBarService.showWarningSnackBar(
                                'We were unable to retrieve your order list please try again.',
                            );
                            return of(
                                new TradingActions.UpdateStatus(),
                                new TradingActions.GetCurrentOrdersFail(),
                            );
                        }),
                    );
            }),
        ),
    );
    
    approveOrder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TradingActions.APPROVE_ORDER),
            switchMap((action: TradingActions.ApproveOrder) => {
                return this.tradingService
                    .approveOrder(
                        action.payload.portfolioId,
                        action.payload.instructionId,
                    )
                    .pipe(
                        map(() => {
                            this.snackBarService.showSnackBar(
                                `Success! You've approved your ${this.orderActionMessage(action.payload.order)} order of ${this.orderQuantityOrAmountMessage(action.payload.order)} of ${action.payload.order.data.securityName}.`,
                            );
                            this.tradingFacade.loadOrders();
                            return new TradingActions.UpdateStatus();
                        }),
                        catchError(() => {
                            this.snackBarService.showWarningSnackBar(
                                `Your approval for ${this.orderQuantityOrAmountMessage(action.payload.order)} of ${action.payload.order.data.securityName} was unable to be placed.
                Please try again.`,
                            );
                            return of(new TradingActions.UpdateStatus());
                        }),
                    );
            }),
        ),
    );

    cancelPendingOrder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TradingActions.CANCEL_PENDING_ORDER),
            switchMap((action: TradingActions.CancelPendingOrder) => {
                return this.tradingService
                    .cancelPendingOrder(
                        action.payload.portfolioId,
                        action.payload.instructionId,
                    )
                    .pipe(
                        map(() => {
                            this.snackBarService.showSnackBar(
                                `Success! You've cancelled your ${this.orderActionMessage(action.payload.order)} order of ${this.orderQuantityOrAmountMessage(action.payload.order)} of ${action.payload.order.data.securityName}.
                The cancellation will be effective immediately`,
                            );
                            this.tradingFacade.loadOrders();
                            return new TradingActions.UpdateStatus();
                        }),
                        catchError(() => {
                            this.snackBarService.showWarningSnackBar(
                                `Your cancellation for ${this.orderQuantityOrAmountMessage(action.payload.order)} of ${action.payload.order.data.securityName} was unable to be placed.
                Please try again.`,
                            );
                            return of(new TradingActions.UpdateStatus());
                        }),
                    );
            }),
        ),
    );

    GetCurrentRegularInvestmentPlan$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TradingActions.GET_REGULAR_INVESTMENT_PLAN),
            deduplicatingSwitchMap((action: TradingActions.GetRegularInvestmentPlan) => {
                return this.tradingService
                    .getRegularInvestmentPlan(action.payload)
                    .pipe(
                        map((res: InvestmentPlan) => {
                            if (res) {
                                return new TradingActions.GetRegularInvestmentPlanSuccess(
                                    res,
                                );
                            }
                        }),
                        catchError((err) => {
                            const name =
                                this.appConfig.getSection(
                                    Features.REGULAR_INVESTMENT_PLAN,
                                ) &&
                                this.appConfig.getSection(
                                    Features.REGULAR_INVESTMENT_PLAN,
                                ).name
                                    ? this.appConfig.getSection(
                                          Features.REGULAR_INVESTMENT_PLAN,
                                      ).name
                                    : 'regular investment plan';
                            if (err.status !== 404) {
                                this.snackBarService.showWarningSnackBar(
                                    `Your ${name} was unable to be retrieved.`,
                                );
                                return of(new TradingActions.UpdateStatus());
                            } else {
                                // plan not found
                                return of(
                                    new TradingActions.GetRegularInvestmentPlanNotFound(),
                                );
                            }
                        }),
                    );
            }),
        ),
    );

    SaveCurrentRegularInvestmentPlan$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TradingActions.SAVE_REGULAR_INVESTMENT_PLAN),
            switchMap((action: TradingActions.SaveRegularInvestmentPlan) => {
                return this.tradingService
                    .saveRegularInvestmentPlan(action.payload)
                    .pipe(
                        concatMap((res: InvestmentPlan) => {
                            if (res) {
                                
                                this.displayRegularInvestmentPlanUpdateSuccessSnackBar(res);

                                const actions = [
                                    new TradingActions.GetRegularInvestmentPlanSuccess(res),
                                    new PortfolioActions.GetInvestorPortfolios(),
                                    new TradingActions.UpdateStatus()
                                ];

                                return actions;
                            }
                        }),
                        catchError((err) => {
                            const name = this.appConfig.getSection(
                                Features.REGULAR_INVESTMENT_PLAN,
                            ).name
                                ? this.appConfig.getSection(
                                      Features.REGULAR_INVESTMENT_PLAN,
                                  ).name
                                : 'regular investment plan';
                            if (err.status === 409) {
                                // Conflict
                                const errorMessage =
                                    err.error.message +
                                    'Please refresh the page.';
                                return of(
                                    new GlobalNotificationActions.NotificationErrorAction(
                                        errorMessage,
                                    ),
                                );
                            }
                            this.snackBarService.showWarningSnackBar(
                                `Your ${name} was unable to be placed. Please try again.`,
                            );
                            return of(
                                new TradingActions.GetRegularInvestmentPlanSuccess(
                                    {
                                        ...action.payload,
                                        status: RegularInvestmentStatus.Incomplete,
                                    },
                                ),
                                new TradingActions.UpdateStatus(),
                            );
                        }),
                    );
            }),
        ),
    );

    private displayRegularInvestmentPlanUpdateSuccessSnackBar(res : InvestmentPlan) : void {
        const regInvestmentConfig =
            this.appConfig.getSection(
                Features.REGULAR_INVESTMENT_PLAN,
            ) as Partial<IRegularInvestmentPlanSetting>;

        const name =
            regInvestmentConfig && regInvestmentConfig.name
                ? regInvestmentConfig.name
                : 'regular investment plan';

        const planLabel =
            regInvestmentConfig && regInvestmentConfig.planLabel
                ? regInvestmentConfig.planLabel
                : 'plan';

        if (
            res.status === RegularInvestmentStatus.Active &&
            res.planType === PlanType.ExplicitAmount
        ) {
            this.snackBarService.showSnackBar(
                `Success! You've set up <b>${name} of ${res.amountValue.toFixed(2)} ${investmentPlanPeriodTypes[res.periodType]}.</b>`,
            );
        } else if (
            res.status === RegularInvestmentStatus.Ready &&
            res.planType === PlanType.ExplicitAmount
        ) {
            this.snackBarService.showSnackBar(
                `Success! <b>${name} of ${res.amountValue.toFixed(2)} ${investmentPlanPeriodTypes[res.periodType]}</b> is now ready to start.</b>`,
            );
        } else if (
            res.status === RegularInvestmentStatus.Active &&
            res.planType === PlanType.AvailableCash
        ) {
            let successMessage = `Success! You've confirmed for us how you would like your ${planLabel} to be invested.`;
            
            if(!!regInvestmentConfig?.investmentStrategyMoreInformationLink)
                successMessage += ` For more information <a href='${regInvestmentConfig?.investmentStrategyMoreInformationLink}' target='_blank'>click here</a>`;

            this.snackBarService.showSnackBar(successMessage);
        } else if (
            res.status === RegularInvestmentStatus.InActive
        ) {
            this.snackBarService.showSnackBar(
                `Success! You've stopped your <b>${name} of ${res.amountValue.toFixed(2)} ${investmentPlanPeriodTypes[res.periodType]}.</b> You can restart it at anytime.`,
            );
        }
    }

    orderActionMessage(order: Order): string {
        switch (order.data.action) {
            case OrderAction.Buy:
                return 'buy';
            case OrderAction.Sell:
                return 'sell';
        }
    }

    // Differenciate the message based on the order type and action
    orderQuantityOrAmountMessage(order: Order): string {
        switch (order.data.orderType) {
            // Equity: Buy & Sell units
            case OrderType.Equity:
                return `${this.decimalPipe.transform(order.data.quantity, '1.4-4')} units`;

            // Fund: Buy $$, Sell units
            case OrderType.Fund:
                if (order.data.action === OrderAction.Buy) {
                    return `${order.data.amount.value} ${order.data.amount.currency}`;
                } else {
                    // Sell
                    return `${this.decimalPipe.transform(order.data.quantity, '1.4-4')} units`;
                }

            // Else: Buy prioritise $$, Sell prioritise units
            default:
                if (order.data.action === OrderAction.Buy) {
                    if (order.data.amount && order.data.amount.value) {
                        return `${order.data.amount.value} ${order.data.amount.currency}`;
                    } else {
                        return `${this.decimalPipe.transform(order.data.quantity, '1.4-4')} units`;
                    }
                } else {
                    if (order.data.quantity) {
                        return `${this.decimalPipe.transform(order.data.quantity, '1.4-4')} units`;
                    } else {
                        return `${order.data.amount.value} ${order.data.amount.currency}`;
                    }
                }
        }
    }
}
